// Semantic validation for RDb payloads, layered on format checks in validate.ts.
//
//   create       - every creator's keyId matches its public key (when declared)
//   add-schema   - when creators non-empty: signature by a creator; when empty:
//                  reject unexpected author/signature fields
//   add-group    - same as add-schema
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { validationFailure, validationOk, } from "@hyper-hyper-space/hhs3_mvt";
import { verifyPayloadSignature, deserializePublicKeyFromBase64, computeKeyId } from "@hyper-hyper-space/hhs3_mvt";
import { validateRDbPayloadFormat } from "./validate.js";
export function validateRDbPayload(payload, context) {
    return __awaiter(this, void 0, void 0, function* () {
        const formatResult = validateRDbPayloadFormat(payload);
        if (!formatResult.valid)
            return formatResult;
        if (context.mode === 'create') {
            return validateCreate(payload, context.ctx);
        }
        const action = payload['action'];
        if (action === 'add-schema') {
            return validateMembership(payload, context.rdb, 'add-schema');
        }
        if (action === 'add-group') {
            return validateMembership(payload, context.rdb, 'add-group');
        }
        return validationFailure(`unknown RDb action '${String(action)}'`, { objectHash: context.rdb.getId() });
    });
}
function validateCreate(create, ctx) {
    var _a;
    const creators = (_a = create.creators) !== null && _a !== void 0 ? _a : [];
    const hashSuite = ctx.getHashSuite();
    const seen = new Set();
    for (const creator of creators) {
        if (seen.has(creator.keyId))
            return validationFailure(`duplicate RDb creator '${creator.keyId}'`);
        seen.add(creator.keyId);
        try {
            const pk = deserializePublicKeyFromBase64(creator.publicKey);
            if (computeKeyId(pk, hashSuite) !== creator.keyId) {
                return validationFailure(`RDb creator keyId '${creator.keyId}' does not match public key`);
            }
        }
        catch (_b) {
            return validationFailure(`RDb creator '${creator.keyId}' public key is invalid`);
        }
    }
    return validationOk();
}
function creatorKeyLookup(creators) {
    return (keyId) => __awaiter(this, void 0, void 0, function* () {
        const creator = creators.find((c) => c.keyId === keyId);
        if (creator === undefined)
            return undefined;
        try {
            return deserializePublicKeyFromBase64(creator.publicKey);
        }
        catch (_a) {
            return undefined;
        }
    });
}
function hasAuthFields(payload) {
    return payload.author !== undefined || payload.signature !== undefined;
}
function validateMembership(payload, rdb, actionLabel) {
    return __awaiter(this, void 0, void 0, function* () {
        const creators = rdb.getCreators();
        const objectHash = rdb.getId();
        if (creators.length === 0) {
            if (hasAuthFields(payload)) {
                return validationFailure(`${actionLabel} must not carry author or signature when the RDb declares no creators`, { objectHash });
            }
            return validationOk();
        }
        if (payload.author === undefined || payload.signature === undefined) {
            return validationFailure(`${actionLabel} requires author and signature when the RDb declares creators`, { objectHash });
        }
        if (!rdb.isCreator(payload.author)) {
            return validationFailure(`${actionLabel} author '${payload.author}' is not an RDb creator`, { objectHash });
        }
        if (!(yield verifyPayloadSignature(payload, creatorKeyLookup(creators)))) {
            return validationFailure(`${actionLabel} signature from '${payload.author}' could not be verified`, { objectHash });
        }
        return validationOk();
    });
}
